import { zodResolver } from '@hookform/resolvers/zod';
import {
  ArrowRight,
  ChartLine,
  Folder,
  Sparkle,
  Users,
  VideoCamera,
} from '@phosphor-icons/react';
import { OnboardingStep } from '@reelplicate/api-client/model';
import {
  getListWorkspacesQueryKey,
  useCreateWorkspace,
  useListWorkspaces,
  useUpdateWorkspace,
} from '@reelplicate/api-client/workspaces';
import {
  Button,
  ChoiceCard,
  ChoiceCardGroup,
  Field,
  FilterChip,
  Inline,
  Stack,
  TextInput,
} from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useId } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';

import { browserTimezone } from '@/features/settings/timezones';

import {
  ROLE_FROM_API,
  ROLE_TO_API,
  siteHost,
  TEAM_SIZE_FROM_API,
  TEAM_SIZE_TO_API,
} from './api-values';
import { SiteInput } from './favicon';
import { OnboardingActions, OnboardingCard, OnboardingHeader } from './onboarding-card';
import { useOnboardingProgress } from './progress';
import {
  type ROLES,
  TEAM_SIZES,
  WorkspaceStepSchema,
  type WorkspaceStepValues,
} from './schemas';
import { ONBOARDING_STEPS, stepPath } from './steps';

const ROLE_OPTIONS: {
  value: (typeof ROLES)[number];
  title: string;
  description: string;
  icon: typeof Sparkle;
}[] = [
  {
    value: 'founder',
    title: 'Founder',
    description: 'Building my own brand',
    icon: Sparkle,
  },
  {
    value: 'marketer',
    title: 'Marketer',
    description: 'In-house marketing team',
    icon: ChartLine,
  },
  {
    value: 'agency',
    title: 'Agency',
    description: 'Managing client brands',
    icon: Users,
  },
  {
    value: 'creator',
    title: 'Creator',
    description: 'Making content for my channels',
    icon: VideoCamera,
  },
];

const TEAM_LABEL: Record<(typeof TEAM_SIZES)[number], string> = {
  '1': 'Just me',
  '2-5': '2–5',
  '6-20': '6–20',
  '21-50': '21–50',
  '50+': '50+',
};

/**
 * Figma "Onboarding — 1 · Workspace" (126:263): workspace name and website
 * side by side, the role Choice Cards
 * (2 × 2, 8px apart), team size Filter Chips, and Continue.
 *
 * Continue creates the user's workspace (or updates it if they came Back)
 * with its website, role and team size, and saves progress at Brand. A new
 * website starts reading it into the brand profile step 2 prefills from.
 */
export function WorkspaceStepPage() {
  const navigate = useNavigate();
  const teamLabelId = useId();
  const form = useForm<WorkspaceStepValues>({
    resolver: zodResolver(WorkspaceStepSchema),
    defaultValues: { name: '', website: '' },
  });
  const queryClient = useQueryClient();
  const { advance } = useOnboardingProgress();
  const { data: workspaces } = useListWorkspaces();
  const existing = workspaces?.[0];
  const create = useCreateWorkspace();
  const update = useUpdateWorkspace();

  // Back from a later step: show the workspace they already made.
  useEffect(() => {
    if (!existing || form.formState.isDirty) return;
    form.reset({
      name: existing.name,
      website: siteHost(existing.website),
      role: existing.role ? ROLE_FROM_API[existing.role] : undefined,
      teamSize: existing.teamSize ? TEAM_SIZE_FROM_API[existing.teamSize] : undefined,
    });
  }, [existing, form]);

  const onSubmit = form.handleSubmit(async ({ name, website, role, teamSize }) => {
    try {
      const workspace =
        existing ??
        (await create.mutateAsync({ data: { name, timezone: browserTimezone() } }));
      // Saving the website (re)starts reading it when it changed.
      await update.mutateAsync({
        id: workspace.id,
        data: {
          name,
          website,
          role: ROLE_TO_API[role],
          teamSize: TEAM_SIZE_TO_API[teamSize],
        },
      });
    } catch {
      form.setError('name', { message: 'Couldn’t save your workspace. Try again.' });
      return;
    }
    await queryClient.invalidateQueries({ queryKey: getListWorkspacesQueryKey() });
    if (await advance(OnboardingStep.BRAND)) await navigate(stepPath(2));
  });

  return (
    <OnboardingCard onSubmit={onSubmit}>
      <OnboardingHeader
        eyebrow={`Step 1 of ${ONBOARDING_STEPS.length}`}
        title="Set up your workspace"
        subtitle="This is where your brand, reels and team live."
      />

      <Stack gap={5}>
        <Stack gap={3} className="tablet:flex-row tablet:items-start">
          <Controller
            name="name"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field
                label="Workspace name"
                error={fieldState.error?.message}
                className="flex-1"
              >
                <TextInput
                  {...field}
                  autoComplete="off"
                  placeholder="Your brand or company"
                  leadingIcon={Folder}
                />
              </Field>
            )}
          />
          <Controller
            name="website"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field
                label="Your website"
                helper="We’ll read it to prefill your brand profile."
                error={fieldState.error?.message}
                className="flex-1"
              >
                <SiteInput
                  {...field}
                  type="url"
                  inputMode="url"
                  autoComplete="off"
                  placeholder="yourbrand.com"
                />
              </Field>
            )}
          />
        </Stack>

        <Controller
          name="role"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field label="What best describes you?" error={fieldState.error?.message}>
              <ChoiceCardGroup
                value={field.value}
                onValueChange={field.onChange}
                name={field.name}
                // Cards in a row share a height so the radios line up.
                className="gap-2 tablet:grid-cols-2"
              >
                {ROLE_OPTIONS.map(({ icon: Icon, ...option }) => (
                  <ChoiceCard
                    key={option.value}
                    value={option.value}
                    title={option.title}
                    description={option.description}
                    visual={<Icon aria-hidden className="text-icon-primary size-8" />}
                  />
                ))}
              </ChoiceCardGroup>
            </Field>
          )}
        />

        <Controller
          name="teamSize"
          control={form.control}
          render={({ field, fieldState }) => (
            <Stack gap={2}>
              <p id={teamLabelId} className="text-label-md">
                Team size
              </p>
              <Inline as="div" role="group" aria-labelledby={teamLabelId} gap={2} wrap>
                {TEAM_SIZES.map((size) => (
                  <FilterChip
                    key={size}
                    selected={field.value === size}
                    onSelectedChange={() => field.onChange(size)}
                  >
                    {TEAM_LABEL[size]}
                  </FilterChip>
                ))}
              </Inline>
              {fieldState.error && (
                <p className="text-caption text-status-danger-fg">
                  {fieldState.error.message}
                </p>
              )}
            </Stack>
          )}
        />
      </Stack>

      <OnboardingActions>
        <Button
          type="submit"
          size="lg"
          trailingIcon={ArrowRight}
          loading={form.formState.isSubmitting}
        >
          Continue
        </Button>
      </OnboardingActions>
    </OnboardingCard>
  );
}
